/** Thin typed wrapper that executes catalog endpoints against the ApiClient. */
import type { ApiClient, RequestOptions } from '../http.js'
import type { Endpoint } from './catalog.js'

export interface CallOptions {
  /** Values for `{param}` placeholders in the endpoint path. */
  params?: Record<string, string | number>
  query?: Record<string, string | number | boolean | undefined>
  body?: unknown
  /** Workspace to bind the access token to (required when endpoint.workspaceBound). */
  workspaceId?: string
  /** Extra scopes on top of the endpoint's declared ones. */
  extraScopes?: string[]
  rawBody?: BodyInit
  contentType?: string
}

export function fillPath(template: string, params: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name]
    if (value === undefined) throw new Error(`Missing path parameter '${name}' for ${template}`)
    // File-system-like path params may contain slashes which must survive.
    return String(value)
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/')
  })
}

function toRequestOptions(endpoint: Endpoint, options: CallOptions): RequestOptions {
  if (endpoint.workspaceBound && !options.workspaceId) {
    throw new Error('This operation requires a workspace. Pass --workspace <id> or set one with `verentis use workspace <id>`.')
  }
  return {
    scopes: [...new Set([...endpoint.scopes, ...(options.extraScopes ?? [])])],
    tokenWorkspaceId: endpoint.workspaceBound ? options.workspaceId : undefined,
    query: options.query,
    body: options.body,
    rawBody: options.rawBody,
    contentType: options.contentType,
  }
}

/** Unwraps the platform's `JsonResponse<T>` (`{ value: T }`) envelope when present. */
function unwrap<T>(payload: unknown): T {
  if (payload !== null && typeof payload === 'object' && !Array.isArray(payload)) {
    const keys = Object.keys(payload as Record<string, unknown>)
    if (keys.length === 1 && keys[0] === 'value') return (payload as { value: T }).value
  }
  return payload as T
}

export async function call<T>(api: ApiClient, endpoint: Endpoint, options: CallOptions = {}): Promise<T> {
  const result = await api.request<unknown>(endpoint.method, fillPath(endpoint.path, options.params), toRequestOptions(endpoint, options))
  return unwrap<T>(result)
}

export async function callRaw(api: ApiClient, endpoint: Endpoint, options: CallOptions = {}): Promise<Response> {
  return api.requestRaw(endpoint.method, fillPath(endpoint.path, options.params), toRequestOptions(endpoint, options))
}

export interface PagedResult<T> {
  totalCount?: number
  pageCount?: number
  pageSize?: number
  pageNumber?: number
  data?: T[]
  /** Some services expose `items` instead of `data`. */
  items?: T[]
}

export const pageItems = <T>(page: PagedResult<T> | T[] | null | undefined): T[] => {
  if (!page) return []
  if (Array.isArray(page)) return page
  return page.data ?? page.items ?? []
}

export interface PagingFlags {
  page?: string
  pageSize?: string
  orderBy?: string
  all?: boolean
}

export function pagingQuery(flags: PagingFlags): Record<string, string | number | undefined> {
  return {
    PageNo: flags.page ? Number(flags.page) : 1,
    PageSize: flags.pageSize ? Number(flags.pageSize) : 50,
    OrderBy: flags.orderBy,
  }
}

/** Fetch every page of a paged endpoint (used by --all). */
export async function callAllPages<T>(api: ApiClient, endpoint: Endpoint, options: CallOptions = {}): Promise<T[]> {
  const items: T[] = []
  let pageNo = 1
  for (;;) {
    const page = await call<PagedResult<T>>(api, endpoint, {
      ...options,
      query: { ...options.query, PageNo: pageNo, PageSize: 100 },
    })
    const batch = pageItems(page)
    items.push(...batch)
    const pageCount = Array.isArray(page) ? 1 : (page.pageCount ?? 1)
    if (batch.length === 0 || pageNo >= pageCount) return items
    pageNo += 1
  }
}
